/* eslint-disable max-lines */
/* eslint-disable max-len */
import PropTypes from 'prop-types';
import { createRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    ProductListContainer as SourceProductListContainer
} from 'SourceComponent/ProductList/ProductList.container';
import { updateCategoryPage } from 'Store/ProductList/ProductList.action';
import { setScrollToElement, updateScroll } from 'Store/Ui/Ui.action';
import BrowserDatabase from 'Util/BrowserDatabase/BrowserDatabase';
// import { SCROLL_TOLERANCE as tolerance } from 'Util/Browser';
import componentLoader from 'Util/componentLoader';
import { shallowEqual } from 'Util/Product';
import { getQueryParam, setQueryParams } from 'Util/Url';

export const ProductListDispatcher = componentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/ProductList/ProductList.dispatcher'
), 2);

/** @namespace Bodypwa/Component/ProductList/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    appendPage: (items, currentPage) => ProductListDispatcher.then(
        ({ default: dispatcher }) => dispatcher.appendPage(items, currentPage, dispatch)
    ),
    cleanProductList: () => ProductListDispatcher.then(
        ({ default: dispatcher }) => dispatcher.cleanProductList(dispatch)
    ),
    setScrollToElement: (element) => dispatch(setScrollToElement(element)),
    updateCategoryPage: (page) => dispatch(updateCategoryPage(page, dispatch)),
    updateScroll: (scroll) => dispatch(updateScroll(scroll))
});
/** @namespace Bodypwa/Component/ProductList/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    isMobile: state.ConfigReducer.device.isMobile,
    currentPage: state.ProductListReducer.currentPage,
    scroll: state.UiReducer.updateScroll
});
/** @namespace Bodypwa/Component/ProductList/Container */
export class ProductListContainer extends SourceProductListContainer {
    static propTypes = {
        ...SourceProductListContainer.propTypes,
        isSwiper: PropTypes.bool
    };

    loadPrevBtn = createRef();

    emptyRef = createRef();

    static defaultProps = {
        ...SourceProductListContainer.defaultProps,
        pageSize: 24,
        isSwiper: false
    };

    state = {
        pagesCount: 1,
        variantsPagesRequested: [],
        pricePagesRequested: [],
        updateScroll: false,
        windowHeight: document.documentElement.scrollHeight,
        prevLoadWindowPosition: false
    };

    _getIsInfiniteLoaderEnabled() {
        // disable infinite scroll on mobile
        const { isInfiniteLoaderEnabled } = this.props;

        return isInfiniteLoaderEnabled;
    }

    __construct(props) {
        super.__construct(props);
        this.isScrolling = null;
    }

    shouldComponentUpdate(nextProps) {
        return !shallowEqual(this.props, nextProps);
    }

    componentDidMount() {
        const { pages, isPreventRequest } = this.props;
        const { pagesCount } = this.state;
        const pagesLength = Object.keys(pages).length;
        if (pagesCount !== pagesLength && pagesLength) {
            this.setState({ pagesCount: pagesLength });
        }

        // Is true when category is changed. This check prevents making new requests when navigating back to PLP from PDP
        if (!isPreventRequest) {
            this.requestPage(this._getPageFromUrl());
        }
    }

    componentDidUpdate(prevProps) {
        const {
            sort,
            search,
            filter,
            filter: {
                customFilters,
                categoryIds
            },
            pages,
            cleanProductList,
            updateScroll,
            totalItems,
            scroll,
            currentPage
        } = this.props;

        const {
            sort: prevSort,
            search: prevSearch,
            filter: prevFilter,
            filter: {
                customFilters: prevCustomFilters,
                categoryIds: prevCategoryIds
            },
            pages: prevPages
        } = prevProps;

        const {
            pagesCount, prevLoadWindowPosition, windowHeight
        } = this.state;
        const pagesLength = Object.keys(pages).length;

        if (pagesCount !== pagesLength && pagesLength) {
            this.setState({ pagesCount: pagesLength });
        }
        if (windowHeight < document.documentElement.scrollHeight) {
            this.setState({ windowHeight: document.documentElement.scrollHeight });
        }

        if (prevLoadWindowPosition && Object.keys(pages).length !== Object.keys(prevPages).length) {
            this.setState({ prevLoadWindowPosition: false });
            const loadPrevBtn = this.loadPrevBtn.current;

            if (loadPrevBtn) {
                const rect = loadPrevBtn.getBoundingClientRect();
                const isVisible = (
                    rect.top >= 0
                && rect.left >= 0
                && rect.bottom <= window.innerHeight
                && rect.right <= window.innerWidth
                );

                if (isVisible) {
                    window.scrollTo(0, document.documentElement.scrollHeight - windowHeight + document.documentElement.scrollTop);
                }
            }
        }

        const isNewSearch = search !== prevSearch;
        const isPageChanged = JSON.stringify(sort) !== JSON.stringify(prevSort);

        if ((isNewSearch
            || isPageChanged
            || JSON.stringify(customFilters) !== JSON.stringify(prevCustomFilters)) && !scroll && categoryIds === prevCategoryIds) {
            updateScroll(true);
        }

        const { originalScrollY, originalPageHeight, categoryId } = BrowserDatabase.getItem('scroll-position') || {};
        if (totalItems && originalScrollY && categoryId === categoryIds && currentPage > 1) {
            setTimeout(() => {
                const newPageHeight = document.documentElement.scrollHeight;
                const scrollDifference = originalPageHeight - newPageHeight;
                const newScrollY = originalScrollY - scrollDifference;
                document.documentElement.scrollTop = newScrollY;
            // eslint-disable-next-line no-magic-numbers
            }, 100);
            BrowserDatabase.deleteItem('scroll-position');
        } else {
            BrowserDatabase.deleteItem('scroll-position');
        }

        if (isNewSearch
            || isPageChanged
            || JSON.stringify(filter) !== JSON.stringify(prevFilter)
        ) {
            cleanProductList();
            this.requestPage(1, false, false, true);
            this.updatePage(1);
        }
    }

    containerFunctions = {
        loadPrevPage: this.loadPage.bind(this, false),
        loadPage: this.loadPage.bind(this),
        updatePage: this.updatePage.bind(this)
    };

    loadPage(next = true) {
        const { pagesCount } = this.state;
        const { isPageLoading } = this.props;

        const {
            minPage,
            maxPage,
            totalPages,
            loadedPagesCount
        } = this._getPagesBounds();

        const isUpdatable = totalPages > 0 && pagesCount === loadedPagesCount;
        const shouldUpdateList = next ? maxPage < totalPages : minPage > 1;

        if (isUpdatable && shouldUpdateList && !isPageLoading) {
            if (!next) {
                this.setState({ prevLoadWindowPosition: true });
            }
            const pageToLoad = next ? maxPage + 1 : minPage - 1;
            this.setState({ pagesCount: pagesCount + 1 });
            this.requestPage(pageToLoad, true);
        }
    }

    requestPage = (currentPage = 1, isNext = false) => {
        const {
            sort,
            search,
            filter,
            pageSize,
            requestProductList,
            requestProductListInfo,
            noAttributes,
            noVariants,
            isWidget
        } = this.props;

        /**
         * In case the wrong category was passed down to the product list,
         * prevent it from being requested.
         */
        if (filter.categoryIds === -1) {
            return;
        }

        /**
         * Do not request page if there are no filters
         */
        if (!search && !this.isEmptyFilter()) {
            return;
        }
        // TODO: the product list should not request itself, when coming from PDP
        const options = {
            isNext,
            noAttributes,
            noVariants,
            args: {
                sort,
                filter,
                search,
                pageSize,
                currentPage
            }
        };

        const infoOptions = {
            args: {
                filter,
                search
            }
        };

        requestProductList(options);

        if (!isWidget) {
            requestProductListInfo(infoOptions);
        }
    };

    _getPageFromUrl(url) {
        const { location: currentLocation } = this.props;
        const location = url || currentLocation;

        return +(getQueryParam('p', location) || 1);
    }

    updatePage(pageNumber) {
        const { location, history, updateCategoryPage } = this.props;
        updateCategoryPage(pageNumber);
        setQueryParams({
            p: pageNumber === 1 ? '' : pageNumber
        }, location, history, false);
    }

    containerProps() {
        const {
            device,
            isLoading,
            isPaginationEnabled,
            isWidget,
            mix,
            pages,
            selectedFilters,
            title,
            totalPages,
            totalItems,
            isSwiper,
            isPageLoading,
            pageSize,
            scroll
        } = this.props;

        return {
            device,
            isLoading,
            isPaginationEnabled,
            isWidget,
            mix,
            pages,
            selectedFilters,
            title,
            totalPages,
            totalItems,
            isSwiper,
            isPageLoading,
            currentPage: this._getPageFromUrl(),
            isShowLoading: this._isShowLoading(),
            isVisible: this._isVisible(),
            requestPage: this.requestPage,
            pageSize,
            loadPrevBtn: this.loadPrevBtn,
            emptyRef: this.emptyRef,
            // disable this property to enable infinite scroll on desktop
            isInfiniteLoaderEnabled: this._getIsInfiniteLoaderEnabled(),
            scroll
        };
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProductListContainer));
